import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useGetCustomer } from 'api/customer';
import { useAuth } from 'auth';
import {
    ActivityStatus,
    getApiRegion,
    getBakerEnv,
    getMockActivityStatus,
    getBaseUrl,
    getMockAthletesStatus,
    AthleteStatusResponse,
} from 'processing-status/activity-status-util';
import { useEffect, useRef } from 'react';

const POLL_RATE_10 = 10000;
const POLL_RATE_5 = 5000;
const env = getBakerEnv();

// API swagger docs: https://sturdy-pancake-3e913kw.pages.github.io/openapi.html

async function getBulkActivityStatus(
    token: string,
    customerId: string,
    region: string,
    activityIds: string[],
    mockMode?: boolean,
) {
    const apiRegion = getApiRegion(region);

    const now = new Date();
    const twoDaysAgo = new Date(new Date().setDate(now.getDate() - 2));

    const path = `/api/activity/bakestatus?StartTime=${twoDaysAgo.toISOString()}&EndTime=${now.toISOString()}`;
    const url = getBaseUrl(apiRegion, env) + path;

    // for local development
    if (mockMode && activityIds) {
        const response = [];

        for (let i = 0; i < activityIds.length; i++) {
            response.push(getMockActivityStatus(activityIds[i]));
        }

        return response;
    }

    try {
        const req = await fetch(url, {
            headers: {
                Authorization: 'Bearer ' + token,
                CustomerId: customerId,
            },
        });

        if (!req.ok) {
            throw new Error('Error calling the Baker API (bakestatus)');
        }

        const res = await req.json();
        return res;
    } catch (error) {
        return [];
    }
}

export const useGetActivityStatus = (activityIds?: string[], mockMode?: boolean) => {
    const user = useGetCustomer();
    const auth = useAuth();
    const queryClient = useQueryClient();
    const prevStatusRef = useRef<ActivityStatus[] | undefined>(undefined);

    const {
        data: statuses,
        error,
        isLoading,
    } = useQuery({
        queryKey: ['activity_status'],
        queryFn: async () => {
            const token = mockMode ? 'no token' : await auth.getAccessToken();

            if (!token || !user.data || !activityIds) {
                return [];
            }

            return getBulkActivityStatus(token, user.data?.id + '', user.data?.aws_region, activityIds, mockMode);
        },
        refetchInterval: POLL_RATE_10,
    });

    /**
     *  check whether we need to invalidate an activity due to a status update
     */
    useEffect(() => {
        if (!statuses || !prevStatusRef.current) {
            prevStatusRef.current = statuses; // initialize first time
            return;
        }

        statuses.forEach((s: ActivityStatus) => {
            const prev = prevStatusRef.current?.find((p) => p.activityId === s.activityId);

            const isNowBaked = s.status?.includes('baked');
            const wasBaked = prev?.status?.includes('baked');

            if (isNowBaked && !wasBaked) {
                queryClient.invalidateQueries({
                    queryKey: ['activities', s.activityId],
                });
                queryClient.invalidateQueries({
                    queryKey: ['activities', 'deep'],
                });
            }
        });

        // update snapshot for next poll
        prevStatusRef.current = statuses;
    }, [statuses, queryClient]);

    return { statuses, error, isLoading };
};

async function getActivityAthletesStatus(
    token: string,
    customerId: string,
    region: string,
    activityId: string,
    mockMode?: boolean,
) {
    const apiRegion = getApiRegion(region);

    const path = `/api/activity/${activityId}/ActivityAthleteBakeStatus`;
    const url = getBaseUrl(apiRegion, env) + path;

    // for local development
    if (mockMode && activityId) {
        const response = getMockAthletesStatus(activityId);
        return response;
    }

    try {
        const req = await fetch(url, {
            headers: {
                Authorization: 'Bearer ' + token,
                CustomerId: customerId,
            },
        });

        if (!req.ok) {
            throw new Error(`Error calling the Baker API (ActivityAthleteBakeStatus) for activity: ${activityId}`);
        }

        const res: AthleteStatusResponse[] = await req.json();
        return res;
    } catch (error) {
        return undefined;
    }
}

/**
 * Polls for the athletes' status of a single activity.
 */
export const useGetActivityAthletesStatus = (activityId: string, mockMode?: boolean) => {
    const user = useGetCustomer();
    const auth = useAuth();

    const isEnabled = !!user.data;

    const activityAthletesStatus = useQuery({
        queryKey: ['activity_athletes_status', activityId],
        queryFn: async () => {
            const token = mockMode ? 'no token' : await auth.getAccessToken();

            if (!token || !user.data) {
                return undefined;
            }

            return getActivityAthletesStatus(token, user.data?.id + '', user.data?.aws_region, activityId, mockMode);
        },
        refetchInterval: POLL_RATE_5,
        enabled: isEnabled,
    });
    return activityAthletesStatus;
};

/**
 * Polls for the athletes' status of multiple activities in parallel, groups results by activityId.
 * useQueries returns an array of query result objects.
 * unlike promise.all, it doesn't 'fail all' if one fails
 * ie it is possible to resolve for N activities, error for 1.
 */
export const useGetActivitiesAthletesStatus = (activityIds: string[], mockMode?: boolean) => {
    const user = useGetCustomer();
    const auth = useAuth();

    const isEnabled = !!user.data && activityIds.length > 0;

    const queryResults = useQueries({
        queries: activityIds.map((id: string) => {
            return {
                queryKey: ['activity_athletes_status', id],
                queryFn: async () => {
                    const token = mockMode ? 'no token' : await auth.getAccessToken();

                    if (!token || !user.data) {
                        return undefined;
                    }

                    return getActivityAthletesStatus(token, user.data?.id + '', user.data?.aws_region, id, mockMode);
                },
                refetchInterval: POLL_RATE_10,
                enabled: isEnabled,
            };
        }),
    });
    const results = queryResults.map((res, i) => {
        return { activityId: activityIds[i], athleteStatuses: res };
    });
    return results;
};
